import { useEffect, useState, useRef } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import StatusBadge from "@/components/StatusBadge";
import { ArrowLeft, Upload, FileText, Download, Briefcase } from "lucide-react";

const STATUSES = ["pending", "assigned", "in_progress", "completed", "cancelled"];

export default function AdminWorkOrderDetail() {
  const { id } = useParams();
  const [wo, setWo] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ status: "", assigned_to: "", admin_notes: "" });
  const photoInput = useRef(null);
  const reportInput = useRef(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [uploadingReport, setUploadingReport] = useState(false);

  const load = () =>
    api.get(`/work-orders/${id}`).then((r) => {
      setWo(r.data);
      setForm({
        status: r.data.status,
        assigned_to: r.data.assigned_to || "",
        admin_notes: r.data.admin_notes || "",
      });
    });

  useEffect(() => {
    load();
  }, [id]);

  const saveStatus = async () => {
    setSaving(true);
    try {
      await api.patch(`/work-orders/${id}`, form);
      toast.success("Work order updated.");
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingPhoto(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("caption", "");
      await api.post(`/work-orders/${id}/photos`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Photo uploaded.");
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploadingPhoto(false);
      if (photoInput.current) photoInput.current.value = "";
    }
  };

  const uploadReport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingReport(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("title", file.name);
      await api.post(`/work-orders/${id}/reports`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Report uploaded.");
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploadingReport(false);
      if (reportInput.current) reportInput.current.value = "";
    }
  };

  const [syncingJobber, setSyncingJobber] = useState(false);
  const syncJobber = async () => {
    setSyncingJobber(true);
    try {
      const { data } = await api.post(`/integrations/jobber/sync-work-order/${id}`);
      toast.success(`Pushed to Jobber (${data.jobber_request_id || "created"}).`);
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSyncingJobber(false);
    }
  };

  if (!wo) return <div className="text-slate-400">Loading…</div>;
  const backend = process.env.REACT_APP_BACKEND_URL;

  return (
    <div className="space-y-8" data-testid="admin-wo-detail">
      <Link to="/admin/work-orders" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-slate-500 hover:text-slate-900">
        <ArrowLeft className="h-3.5 w-3.5" /> All work orders
      </Link>

      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.28em] text-slate-500 font-mono-alt">WO-{wo.id.slice(-6).toUpperCase()}</div>
          <h1 className="font-display text-3xl md:text-4xl tracking-tighter font-medium mt-2">{wo.property_address}</h1>
          <div className="mt-2 text-sm text-slate-500">
            {wo.client_company || wo.client_name} · {wo.client_email}
          </div>
        </div>
        <StatusBadge status={wo.status} />
      </div>

      <div className="grid md:grid-cols-3 gap-6">
        {/* Update controls */}
        <div className="md:col-span-1 space-y-6">
          <div className="bg-white border border-slate-200 p-5 space-y-4">
            <div className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">Update work order</div>
            <label className="block">
              <span className="block text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold mb-1.5">Status</span>
              <select
                data-testid="admin-update-status"
                value={form.status}
                onChange={(e) => setForm({ ...form, status: e.target.value })}
                className="w-full px-3 py-2 bg-white border border-slate-300 outline-none text-sm rounded-sm"
              >
                {STATUSES.map((s) => (
                  <option key={s} value={s}>{s.replace("_", " ")}</option>
                ))}
              </select>
            </label>
            <label className="block">
              <span className="block text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold mb-1.5">Assigned to</span>
              <input
                data-testid="admin-assigned-to"
                type="text"
                value={form.assigned_to}
                onChange={(e) => setForm({ ...form, assigned_to: e.target.value })}
                placeholder="Technician name"
                className="w-full px-3 py-2 bg-white border border-slate-300 outline-none text-sm rounded-sm"
              />
            </label>
            <label className="block">
              <span className="block text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold mb-1.5">Admin notes</span>
              <textarea
                data-testid="admin-notes-input"
                value={form.admin_notes}
                onChange={(e) => setForm({ ...form, admin_notes: e.target.value })}
                rows={4}
                className="w-full px-3 py-2 bg-white border border-slate-300 outline-none text-sm rounded-sm"
              />
            </label>
            <button
              data-testid="admin-save-btn"
              onClick={saveStatus}
              disabled={saving}
              className="w-full px-4 py-2 bg-[#1E40AF] hover:bg-[#1E3A8A] text-white text-sm font-semibold rounded-sm disabled:opacity-60"
            >
              {saving ? "Saving…" : "Save changes"}
            </button>
          </div>

          <div className="bg-white border border-slate-200 p-5 space-y-3">
            <div className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">Jobber</div>
            {wo.jobber_request_id ? (
              <div className="text-xs text-slate-600">
                Linked · <span className="font-mono-alt text-slate-800">{wo.jobber_request_id}</span>
                {wo.jobber_last_sync && (
                  <div className="text-[10px] text-slate-400 mt-0.5">
                    Last sync: {new Date(wo.jobber_last_sync).toLocaleString()}
                  </div>
                )}
              </div>
            ) : (
              <div className="text-xs text-slate-500">Not yet pushed to Jobber.</div>
            )}
            <button
              data-testid="sync-jobber-btn"
              onClick={syncJobber}
              disabled={syncingJobber}
              className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 border border-slate-300 text-xs font-semibold text-slate-700 hover:bg-slate-50 rounded-sm disabled:opacity-60"
            >
              <Briefcase className="h-3.5 w-3.5" />
              {syncingJobber ? "Pushing…" : wo.jobber_request_id ? "Re-push to Jobber" : "Push to Jobber"}
            </button>
          </div>

          <div className="bg-white border border-slate-200 p-5 space-y-3">
            <div className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">Details</div>
            <dl className="text-sm divide-y divide-slate-100">
              <Row k="Service" v={wo.service_type} />
              <Row k="Priority" v={<span className="capitalize">{wo.priority}</span>} />
              <Row k="Requested" v={wo.requested_date || "—"} />
              <Row k="Created" v={new Date(wo.created_at).toLocaleString()} />
            </dl>
          </div>
        </div>

        <div className="md:col-span-2 space-y-6">
          <div className="bg-white border border-slate-200">
            <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
              <div className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">Client notes</div>
            </div>
            <div className="p-5">
              {wo.notes ? <p className="text-sm text-slate-700 leading-relaxed">{wo.notes}</p> : <div className="text-sm text-slate-400">No notes provided.</div>}
            </div>
          </div>

          <div className="bg-white border border-slate-200">
            <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
              <div className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">Photos ({wo.photos?.length || 0})</div>
              <label className="inline-flex items-center gap-2 px-3 py-1.5 border border-slate-300 text-xs font-semibold rounded-sm cursor-pointer hover:bg-slate-50">
                <Upload className="h-3.5 w-3.5" /> {uploadingPhoto ? "Uploading…" : "Upload photo"}
                <input
                  data-testid="upload-photo-input"
                  ref={photoInput}
                  type="file"
                  accept="image/*"
                  onChange={uploadPhoto}
                  className="hidden"
                />
              </label>
            </div>
            <div className="p-5">
              {!wo.photos?.length && <div className="text-sm text-slate-400">No photos uploaded.</div>}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {wo.photos?.map((p) => (
                  <a key={p.id} href={`${backend}${p.url}`} target="_blank" rel="noreferrer" className="block border border-slate-200 aspect-square overflow-hidden">
                    <img src={`${backend}${p.url}`} alt={p.caption} className="w-full h-full object-cover" />
                  </a>
                ))}
              </div>
            </div>
          </div>

          <div className="bg-white border border-slate-200">
            <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
              <div className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">Reports ({wo.reports?.length || 0})</div>
              <label className="inline-flex items-center gap-2 px-3 py-1.5 border border-slate-300 text-xs font-semibold rounded-sm cursor-pointer hover:bg-slate-50">
                <Upload className="h-3.5 w-3.5" /> {uploadingReport ? "Uploading…" : "Upload PDF"}
                <input
                  data-testid="upload-report-input"
                  ref={reportInput}
                  type="file"
                  accept="application/pdf"
                  onChange={uploadReport}
                  className="hidden"
                />
              </label>
            </div>
            <div className="p-5">
              {!wo.reports?.length && <div className="text-sm text-slate-400">No reports uploaded.</div>}
              <div className="divide-y divide-slate-100">
                {wo.reports?.map((r) => (
                  <a key={r.id} href={`${backend}${r.url}`} target="_blank" rel="noreferrer" className="flex items-center justify-between py-3 hover:text-[#1E40AF]">
                    <div className="flex items-center gap-3">
                      <FileText className="h-5 w-5 text-slate-500" />
                      <div>
                        <div className="text-sm font-medium">{r.title}</div>
                        <div className="text-xs text-slate-500">{new Date(r.uploaded_at).toLocaleString()}</div>
                      </div>
                    </div>
                    <Download className="h-4 w-4" />
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div className="py-2 flex items-baseline justify-between gap-4">
      <dt className="text-[11px] uppercase tracking-[0.14em] text-slate-500">{k}</dt>
      <dd className="text-slate-800 text-right">{v}</dd>
    </div>
  );
}
